import { useEffect, useRef } from 'react';
import { Marker, Popup } from 'react-leaflet';
import { DivIcon, Marker as LeafletMarker } from 'leaflet';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { searchPinCleared } from '../../store/slices/mapViewSlice';
import { useTheme } from '../../hooks/useTheme';

/**
 * Marks where a location search landed (#523).
 *
 * Searching used to move the viewport and nothing else. At street zoom on a
 * dark basemap the centre of the screen is not a landmark, so an operator who
 * searched an address got a plausible-looking map and no way to tell which
 * building it meant — or whether the geocoder had understood them at all. The
 * answer was on screen and unmarked.
 *
 * Deliberately not a drone marker: a different glyph, a different colour, and
 * no clustering. A pin that could be mistaken for an aircraft on a fleet map is
 * worse than no pin.
 *
 * Renders nothing when no search has landed; must live inside a `MapContainer`.
 */
export const SearchResultMarker: React.FC = () => {
  const pin = useSelector((state: RootState) => state.mapView.searchPin);
  const { isDarkMode } = useTheme();
  const dispatch = useDispatch();
  const markerRef = useRef<LeafletMarker | null>(null);

  // Open the popup as the pin drops. The label is the whole point — it is what
  // tells the operator the geocoder understood "Marszałkowska 1" the way they
  // meant it — so it should not need a click to read.
  useEffect(() => {
    if (pin && markerRef.current) {
      markerRef.current.openPopup();
    }
  }, [pin]);

  if (!pin) {
    return null;
  }

  // Amber, and a pin rather than an aircraft: the two glyphs on this map must
  // not be confusable at a glance.
  const accent = isDarkMode ? '#ffb300' : '#ef6c00';
  const icon = new DivIcon({
    className: 'map-search-result-marker',
    html: `
      <div role="img" aria-label="Search result: ${pin.label.replace(/"/g, '&quot;')}" style="
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        background: ${accent};
        border: 2px solid ${isDarkMode ? '#1e1e1e' : '#ffffff'};
        box-shadow: 0 2px 8px rgba(0,0,0,0.4);
      "></div>`,
    iconSize: [28, 28],
    // Bottom-centre of the teardrop, so the point sits on the coordinate
    // rather than the centre of the blob sitting near it.
    iconAnchor: [14, 28],
    popupAnchor: [0, -28],
  });

  return (
    <Marker
      ref={markerRef}
      position={[pin.lat, pin.lng]}
      icon={icon}
      // Above drone markers: it is transient, and the operator just asked for it.
      zIndexOffset={1000}
    >
      <Popup>
        <div>
          <strong>{pin.label}</strong>
          <br />
          {/* The coordinates, because an address is a claim about a place and
              this is the place. Six decimals is ~0.1 m. */}
          {pin.lat.toFixed(6)}, {pin.lng.toFixed(6)}
          <br />
          <button
            type="button"
            onClick={() => dispatch(searchPinCleared())}
            style={{
              marginTop: 6,
              background: 'none',
              border: 'none',
              padding: 0,
              color: accent,
              cursor: 'pointer',
              textDecoration: 'underline',
              font: 'inherit',
            }}
          >
            Clear marker
          </button>
        </div>
      </Popup>
    </Marker>
  );
};
